import { Plugin } from '@nuxt/types'
import Cookies from 'js-cookie'
import { GtmPlugin, GtmEventNames } from '~/utils/tracking/gtm'

export enum AB_TESTS_NAME {
  EXPOSED_USERS_TEST = '0',
  CATEGORY_SORTING = '51',
  BF_SORTING = '55',
  SEARCH = '59',
  HP_PRODUCT_BOX = '70',
  CATEGORY_ADVISOR = '78',
  CATEGORY_PRODUCT_BOX_REDESIGN = '128',
  SEARCH_SUGGESTER = '95',
  SEARCH_DYNAMIC_FILTERS = '108',
  MOBILE_MENU = '112',
  MODAL_FILTERS = '114',
  DETAIL_BOTTOM_REDESIGN = '122',
  UP_SELL_CROSS_SELL = '115',
  COMPACT_SUGGESTER = '117',
  RETURN_SHIPMENTS = '127',
  QU_REDIRECT = '125',
}

export enum AB_TEST_VARIANT {
  A = 'a',
  B = 'b',
  C = 'c',
  D = 'd',
  E = 'e',
}
export interface AbTesting {
  trackActiveABTest: (
    abTestId: AB_TESTS_NAME | number,
    lazyComponent?: boolean
  ) => void
  unTrackActiveABTest: (
    abTestId: AB_TESTS_NAME,
    lazyComponent?: boolean
  ) => void
  clearActiveTestCookies: () => void
  setMgTestsCookies: () => void
  getABtestVariant: (
    abTestId: AB_TESTS_NAME,
    lazyComponent?: boolean
  ) => string | undefined
  getABtestVariantSimple: (abTestId: AB_TESTS_NAME) => string | undefined
  getABtestVariantByName: (
    abTestId: AB_TESTS_NAME,
    testVariant: AB_TEST_VARIANT,
    lazyComponent?: boolean
  ) => boolean | undefined
  searchAbTestCookie: (testId: string) => string | undefined
  searchAbTestMirror: () => boolean
  searchABTest77: (abTestValue: string | undefined) => string | undefined
}

const MG_TESTS_COOKIE = '_mgTests'
const MG_TESTS_ACTIVE_COOKIE = '_mgTestsActive'

export const addActiveTests = (abTestWithVariant: string) => {
  const activeTestsCookie = Cookies.get(MG_TESTS_ACTIVE_COOKIE)

  if (
    activeTestsCookie?.length &&
    activeTestsCookie?.includes(abTestWithVariant)
  ) {
    return
  }

  if (!activeTestsCookie) {
    Cookies.set(MG_TESTS_ACTIVE_COOKIE, abTestWithVariant)
    return true
  }

  const parsedCookies = activeTestsCookie.split('.')
  parsedCookies.push(abTestWithVariant)
  const sortedCookies = [...new Set(parsedCookies)].sort().join('.')
  Cookies.set(MG_TESTS_ACTIVE_COOKIE, sortedCookies)
  return true
}

export const removeActiveTests = (abTestWithVariant: string) => {
  const activeTestsCookie = Cookies.get(MG_TESTS_ACTIVE_COOKIE)

  if (!activeTestsCookie) {
    return
  }

  const parsedCookies = activeTestsCookie.split('.')
  const variantIndex = parsedCookies.indexOf(abTestWithVariant)

  if (variantIndex === -1) {
    return
  }

  parsedCookies.splice(variantIndex, 1)
  if (parsedCookies.length > 0) {
    const sortedCookies = [...new Set(parsedCookies)].sort().join('.')
    Cookies.set(MG_TESTS_ACTIVE_COOKIE, sortedCookies)
  } else {
    Cookies.remove(MG_TESTS_ACTIVE_COOKIE)
  }
  return true
}

export const trackTest = (
  gtm: GtmPlugin,
  abTestWithVariant: string,
  lazyComponent: boolean = false
) => {
  const isActiveTestCookieUpdated = addActiveTests(abTestWithVariant)
  if (lazyComponent && isActiveTestCookieUpdated) {
    gtm.push({ event: GtmEventNames.ACTIVE_AB_TESTS_COOKIE })
  }
}

export const unTrackTest = (
  gtm: GtmPlugin,
  abTestWithVariant: string,
  lazyComponent: boolean = false
) => {
  const isActiveTestCookieUpdated = removeActiveTests(abTestWithVariant)
  if (lazyComponent && isActiveTestCookieUpdated) {
    gtm.push({ event: GtmEventNames.ACTIVE_AB_TESTS_COOKIE })
  }
}

const abTest: Plugin = (context, inject) => {
  const getABtestVariantSimple = (abTestId: AB_TESTS_NAME) => {
    const tests = context.app.$shopConfig?.abTests.cookies.abTests?.tests

    if (!tests) {
      return
    }

    const variant = tests[`_mgExp-${abTestId}`]

    return variant
  }

  const getABtestVariant = (
    abTestId: AB_TESTS_NAME,
    lazyComponent?: boolean
  ) => {
    const variant = getABtestVariantSimple(abTestId)

    if (!variant) {
      return
    }

    const abTestWithVariant = `${abTestId}-${variant}`

    if (process.client && variant) {
      trackTest(context.app.$gtm as GtmPlugin, abTestWithVariant, lazyComponent)
    }

    return variant
  }

  const abTestingFunctions: AbTesting = {
    clearActiveTestCookies() {
      Cookies.remove(MG_TESTS_ACTIVE_COOKIE)
      Cookies.remove(MG_TESTS_COOKIE)
    },

    getABtestVariant,
    getABtestVariantSimple,

    getABtestVariantByName(
      abTestId: AB_TESTS_NAME,
      testVariant: string,
      lazyComponent?: boolean
    ) {
      const variant = getABtestVariant(abTestId, lazyComponent)
      return testVariant === variant
    },

    setMgTestsCookies() {
      if (process.client) {
        const { tests } = context.nuxtState.shopConfig.abTests.cookies.abTests
        if (!Object.keys(tests).length) {
          return
        }
        const testsCookieString = Object.entries(tests)
          .map(([key, value]) => {
            const num = key.substring(key.indexOf('-') + 1)
            return [num, value]
          })
          .sort()
          .map(([key, value]) => key + '-' + value)
          .join('.')

        Cookies.set(MG_TESTS_COOKIE, testsCookieString)
      }
    },

    trackActiveABTest(
      abTestId: AB_TESTS_NAME | number,
      lazyComponent: boolean = false
    ) {
      if (!abTestId) {
        return
      }

      if (process.client) {
        const { tests } = context.nuxtState.shopConfig.abTests.cookies.abTests
        const variant = tests[`_mgExp-${abTestId}`]

        if (!variant) {
          return
        }

        const abTestWithVariant = `${abTestId}-${variant}`
        trackTest(
          context.app.$gtm as GtmPlugin,
          abTestWithVariant,
          lazyComponent
        )
      }
    },

    unTrackActiveABTest(
      abTestId: AB_TESTS_NAME,
      lazyComponent: boolean = false
    ) {
      if (process.client) {
        const variant = getABtestVariant(abTestId)

        if (!variant) {
          return
        }

        const abTestWithVariant = `${abTestId}-${variant}`
        unTrackTest(
          context.app.$gtm as GtmPlugin,
          abTestWithVariant,
          lazyComponent
        )
      }
    },

    searchAbTestCookie(testId) {
      return context.app.$shopConfig?.abTests.cookies.abTests?.tests[
        `_mgExp-${testId}`
      ]
    },

    searchAbTestMirror() {
      return (
        context.app.$shopConfig?.abTests.cookies.search.searchMirror ?? false
      )
    },

    searchABTest77(abTestValue: string | undefined) {
      if (!abTestValue) {
        return
      }

      return abTestValue === AB_TEST_VARIANT.B
        ? AB_TEST_VARIANT.B
        : AB_TEST_VARIANT.A
    },
  }

  if (process.client) {
    abTestingFunctions.clearActiveTestCookies()
    abTestingFunctions.setMgTestsCookies()
  }

  inject('abTesting', abTestingFunctions)
  // @ts-ignore
  context.$abTesting = abTestingFunctions
}

export default abTest
